'use client';

import { useEffect, useRef, useState } from 'react';
import NextImage from 'next/image';
import { cn } from '@/app/lib/utils/utils';
import {
    createSanityImageLoader,
    SanityImageSource,
    urlForImage,
} from '@/sanity/lib/image';

const HERO_IMAGE_MAX_WIDTH = 1600;

const getHeroImageQuality = (width: number) => {
    if (width <= 768) {
        return 60;
    }

    if (width <= 1200) {
        return 68;
    }

    return 75;
};

interface SlugDocumentHeroImageProps {
    image: SanityImageSource;
    title: string;
}

export default function SlugDocumentHeroImage({
    image,
    title,
}: Readonly<SlugDocumentHeroImageProps>) {
    const imageUrl = urlForImage(image, {
        quality: getHeroImageQuality(HERO_IMAGE_MAX_WIDTH),
        width: HERO_IMAGE_MAX_WIDTH,
    });
    const imageLoader = createSanityImageLoader(image, {
        fit: 'max',
        maxWidth: HERO_IMAGE_MAX_WIDTH,
        resolveQuality: getHeroImageQuality,
    });
    const [imageLoaded, setImageLoaded] = useState(!imageUrl);
    const imageRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
        setImageLoaded(!imageUrl);
    }, [imageUrl]);

    useEffect(() => {
        const currentImage = imageRef.current;
        if (!currentImage) {
            return;
        }

        if (currentImage.complete && currentImage.naturalWidth > 0) {
            setImageLoaded(true);
        }
    }, [imageUrl]);

    if (!imageUrl) {
        return null;
    }

    return (
        <div
            className="tablet:mt-[32px] desktop:mt-[40px] relative mt-[24px] w-[100vw] max-w-[980px]"
            style={{ aspectRatio: '98/46' }}
        >
            {!imageLoaded && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 z-10 overflow-hidden rounded-[16px] bg-[#F1F1F1] before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent"
                />
            )}
            <NextImage
                ref={imageRef}
                fill
                priority
                loader={imageLoader}
                src={imageUrl}
                alt={title}
                sizes="(max-width: 767px) 100vw, (max-width: 980px) 87.5vw, 980px"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
                className={cn(
                    'object-contain object-center transition-opacity duration-300',
                    imageLoaded ? 'opacity-100' : 'opacity-0',
                )}
            />
        </div>
    );
}
